import React, { useState, useEffect, useCallback } from 'react';

const FAQ = () => {
  // State to track which questions are expanded
  const [expandedQuestions, setExpandedQuestions] = useState({});

  // Helper to determine if an answer contains HTML
  const isHtmlAnswer = (answer) => answer.trim().startsWith('<');

  // Function to create an ID from a question (for hash linking)
  const createQuestionId = (question) =>
      question
          .toLowerCase()
          .replace(/[^\w\s]/gi, '')
          .replace(/\s+/g, '-');

  // FAQ data
  const faqItems = [
    {
      question: "What is Post‑Quantum Cryptography (PQC)?",
      answer: "Post‑Quantum Cryptography (PQC) comprises cryptographic algorithms designed to resist attacks carried out with large‑scale, fault‑tolerant quantum computers. Unlike today's widely used public‑key algorithms (RSA, Diffie–Hellman and elliptic‑curve cryptography), PQC remains secure even against an adversary equipped with powerful quantum capabilities."
    },
    {
      question: "Why is PQC necessary?",
      answer: "Quantum computers capable of running Shor's algorithm could factor large integers and compute discrete logarithms exponentially faster than classical computers, rendering current public‑key cryptography vulnerable. Threat actors are already harvesting encrypted traffic today with the intention of decrypting it later (\"harvest‑now‑decrypt‑later\"). Deploying PQC protects data with long confidentiality lifetimes and avoids future compliance and liability risks."
    },
    {
      question: "I do not see a PQC key exchange when I test a site in my Windows browser. Why not?",
      answer: "As of May 2025, the Windows versions of Microsoft Edge, Google Chrome and Mozilla Firefox ship with PQC disabled because the underlying Windows Schannel/TLS stack does not yet expose PQC cipher suites. Consequently, a standard TLS inspection on Windows cannot show the PQC key exchange group even if the server offers one. Our web application therefore performs its own standards‑compliant TLS handshake—independent of the host operating system—so that it can detect PQC support accurately."
    },
    {
      question: "Which browsers and platforms currently negotiate PQC?",
      answer: `
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-100">
              <th class="border p-2 text-left">Platform</th>
              <th class="border p-2 text-left">Browser</th>
              <th class="border p-2 text-left">Status</th>
              <th class="border p-2 text-left">Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="border p-2">Linux / macOS</td>
              <td class="border p-2">Chrome 122+</td>
              <td class="border p-2 font-bold">Supported (flag)</td>
              <td class="border p-2">Hybrid X25519+Kyber768</td>
            </tr>
            <tr>
              <td class="border p-2">Linux / macOS</td>
              <td class="border p-2">Firefox 125+</td>
              <td class="border p-2 font-bold">Supported (about:config)</td>
              <td class="border p-2">Hybrid X25519+Kyber768</td>
            </tr>
            <tr>
              <td class="border p-2">Android 15 preview</td>
              <td class="border p-2">Chrome</td>
              <td class="border p-2 font-bold">Enabled by default</td>
              <td class="border p-2"></td>
            </tr>
            <tr>
              <td class="border p-2">iOS / iPadOS 17</td>
              <td class="border p-2">Safari</td>
              <td class="border p-2 font-bold">Not yet</td>
              <td class="border p-2"></td>
            </tr>
            <tr>
              <td class="border p-2">Windows 10/11</td>
              <td class="border p-2">Edge / Chrome / Firefox</td>
              <td class="border p-2 font-bold">Not yet</td>
              <td class="border p-2">Depends on Schannel</td>
            </tr>
          </tbody>
        </table>
        <div class="bg-blue-50 p-4 border-l-4 border-blue-500 my-4">
          <p class="font-bold">Tip:</p>
          <p>Use our built‑in test client or a Linux/macOS browser with PQC enabled to double‑check your site's configuration.</p>
        </div>
      `
    },
    {
      question: "What is a \"key exchange group\", and why does it matter?",
      answer: "During a TLS handshake the client and server agree on a key exchange group—a mathematical structure (e.g., X25519, secp256r1, or Kyber768) used to derive the shared session key. If the group is classical‑only, the confidentiality of that session depends on the hardness of discrete‑log problems; a PQC group such as Kyber768 or a hybrid group (classical + quantum) provides forward secrecy even against quantum adversaries."
    },
    {
      question: "Will adopting PQC degrade performance?",
      answer: "Current hybrid handshakes add 1‑2 KB to the TLS ClientHello and ServerHello messages and increase CPU cost by roughly 1 ms per handshake on modern hardware—negligible for most workloads. Early field tests show no measurable impact on page‑load times."
    },
    {
      question: "Which PQC algorithms should we deploy?",
      answer: `
        <p>The U.S. National Institute of Standards and Technology (NIST) announced draft standards for:</p>
        <ul class="list-disc pl-6 my-2">
          <li><strong>CRYSTALS‑Kyber</strong> (key encapsulation)</li>
          <li><strong>CRYSTALS‑Dilithium</strong>, <strong>FALCON</strong> and <strong>SPHINCS+</strong> (signatures)</li>
        </ul>
        <p>Industry is converging on <strong>Hybrid TLS</strong> with X25519 + Kyber768 while formal standards are being finalised (RFC 9437, draft‑ietf‑tls‑hybrid‑design‑05).</p>
      `
    },
    {
      question: "How can our organisation prepare for the transition?",
      answer: `
        <ol class="list-decimal pl-6 my-2">
          <li><strong>Inventory cryptography</strong> – identify where public‑key algorithms are used in your products, services and supply chain.</li>
          <li><strong>Enable hybrid mode in test environments</strong> – evaluate Kyber‑based key exchange with our free testing tool.</li>
          <li><strong>Update PKI and hardware</strong> – ensure HSMs, smart cards and gateways are on vendor roadmaps for PQC.</li>
          <li><strong>Adopt crypto‑agility</strong> – design systems so algorithms can be swapped via configuration rather than code changes.</li>
          <li><strong>Plan for compliance</strong> – regulators such as ENISA and NIST may set aggressive migration timelines.</li>
        </ol>
        <p>For tailored guidance or a readiness workshop, <a href="/contact" class="text-blue-600 hover:underline">visit our contact page</a>.</p>
      `
    },
    {
      question: "How does this web application detect PQC?",
      answer: `
        <p>The application initiates a TLS 1.3 handshake offering standard and PQC cipher suites. It inspects the server‐selected key exchange group and reports whether it is:</p>
        <ul class="list-disc pl-6 my-2">
          <li>A classical group only (e.g., X25519)</li>
          <li>A PQC‑only group (e.g., Kyber768)</li>
          <li>A hybrid group (e.g., X25519MLKEM768)</li>
        </ul>
      `
    },
    {
      question: "Can I test my own website or internal service?",
      answer: "Absolutely. Enter the hostname—and port if non‑standard—on our home page. For internal services located behind a firewall, download our CLI scanner and run it from within your network."
    },
    {
      question: "What are the next milestones on the PQC roadmap?",
      answer: `
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-100">
              <th class="border p-2 text-left">Date</th>
              <th class="border p-2 text-left">Milestone</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="border p-2">2024‑12</td>
              <td class="border p-2">Final NIST PQC algorithm specifications (FIPS draft)</td>
            </tr>
            <tr>
              <td class="border p-2">2025‑06</td>
              <td class="border p-2">TLS WG finalises hybrid key exchange RFC</td>
            </tr>
            <tr>
              <td class="border p-2">2026</td>
              <td class="border p-2">Major browser vendors enable PQC by default</td>
            </tr>
            <tr>
              <td class="border p-2">2027+</td>
              <td class="border p-2">Classical public‑key algorithms deprecated in new standards</td>
            </tr>
          </tbody>
        </table>
      `
    },
    {
      question: "Where can I learn more?",
      answer: `
        <ul class="list-disc pl-6 my-2">
          <li>NIST PQC Portal – <a href="https://csrc.nist.gov/projects/post‑quantum‑cryptography" class="text-blue-600 hover:underline">https://csrc.nist.gov/projects/post‑quantum‑cryptography</a></li>
          <li>IETF TLS Working Group – <a href="https://datatracker.ietf.org/wg/tls/about/" class="text-blue-600 hover:underline">https://datatracker.ietf.org/wg/tls/about/</a></li>
          <li>ENISA Report "Post‑Quantum Crypto" – <a href="https://www.enisa.europa.eu" class="text-blue-600 hover:underline">https://www.enisa.europa.eu</a></li>
        </ul>
        <p>For any other questions, please <a href="/contact" class="text-blue-600 hover:underline">contact us</a>.</p>
      `
    }
  ];

  // Function to toggle question expansion
  const toggleQuestion = useCallback((id) => {
    const newExpandedState = !expandedQuestions[id];

    setExpandedQuestions(prev => ({
      ...prev,
      [id]: newExpandedState
    }));

    // Update URL with hash when expanding, remove hash when collapsing
    if (newExpandedState) {
      // Add the hash to the URL without causing a page reload
      window.history.pushState(null, '', `#${id}`);

      // Update page title for SEO when expanding a question
      const question = faqItems.find(item => createQuestionId(item.question) === id);
      if (question) {
        document.title = `FAQ: ${question.question} | Quantum Scanner`;
      }
    } else if (window.location.hash === `#${id}`) {
      // Remove the hash from the URL if this question is being collapsed
      window.history.pushState(null, '', window.location.pathname);

      // Reset title when collapsing
      document.title = "Frequently Asked Questions | Quantum Scanner";
    }
  }, [expandedQuestions]);

  // Effect to handle hash navigation (including runtime hash changes)
  useEffect(() => {
    const expandFromHash = () => {
      if (typeof window !== 'undefined' && window.location.hash) {
        const questionId = window.location.hash.substring(1);
        setExpandedQuestions(prev => ({ ...prev, [questionId]: true }));
        const element = document.getElementById(questionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });

        // Update page title for SEO if a question is expanded via hash
        const question = faqItems.find(item => createQuestionId(item.question) === questionId);
        if (question) {
          document.title = `FAQ: ${question.question} | Quantum Scanner`;
        }
      } else {
        // Reset title if no hash is present
        document.title = "Frequently Asked Questions | Quantum Scanner";
      }
    };

    expandFromHash();
    window.addEventListener('hashchange', expandFromHash);
    return () => {
      window.removeEventListener('hashchange', expandFromHash);
    };
  }, []);

  return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">Post‑Quantum Cryptography (PQC) – Frequently Asked Questions</h1>
        <p className="text-gray-600 italic mb-8">Last updated: 28 May 2025</p>


        <div className="space-y-4">
          {faqItems.map((item) => {
            const questionId = createQuestionId(item.question);
            return (
                <div
                    key={questionId}
                    id={questionId}
                    className="bg-white shadow-md rounded-lg overflow-hidden"
                >
                  <button
                      className="w-full text-left p-4 flex justify-between items-center focus:outline-none"
                      onClick={() => toggleQuestion(questionId)}
                      aria-expanded={!!expandedQuestions[questionId]}
                      aria-controls={`${questionId}-panel`}
                  >
                    <h2 className="text-xl font-semibold" id={`${questionId}-heading`}>{item.question}</h2>
                    <div className="flex items-center">
                      {/* Copy Link Button */}
                      <button
                        className="mr-3 text-gray-500 hover:text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = `${window.location.origin}/faq#${questionId}`;
                          navigator.clipboard.writeText(url);
                          // Optional: You could add a tooltip or notification here
                          alert('Link copied to clipboard!');
                        }}
                        aria-label="Copy link to this question"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                        </svg>
                      </button>
                      {/* Expand/Collapse Arrow */}
                      <svg
                          className={`w-6 h-6 transform transition-transform ${expandedQuestions[questionId] ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </button>

                  {expandedQuestions[questionId] && (
                      <div
                          className="p-4 border-t"
                          id={`${questionId}-panel`}
                          role="region"
                          aria-labelledby={`${questionId}-heading`}
                      >
                        {isHtmlAnswer(item.answer) ? (
                            <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                        ) : (
                            <p>{item.answer}</p>
                        )}
                      </div>
                  )}
                </div>
            );
          })}
        </div>
      </div>
  );
};

export default FAQ;
